import type { MetricResult } from '@/types/gmb';
import {
  METRIC_DEFINITIONS,
  METRIC_STATUS_DOT,
  METRIC_STATUS_LABEL,
  METRIC_STATUS_STYLE,
} from '@/constants/gmbMetrics';

function resolveMetric(defCode: string, defName: string, defMax: number, list: MetricResult[]): MetricResult {
  const found = list.find((m) => m.code === defCode);
  if (found) return found;
  return {
    code: defCode,
    name: defName,
    score: null,
    maxScore: defMax,
    status: 'na',
    reasonCode: 'DATA_UNAVAILABLE',
    description: '公開資料不足，尚無法計算此指標。',
  };
}

function MetricItem({ metric }: { metric: MetricResult }) {
  const isNa = metric.status === 'na' || metric.score === null;
  const percent = isNa ? 0 : Math.max(0, Math.min(100, (metric.score! / metric.maxScore) * 100));

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 md:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-400">{metric.code}</p>
          <h3 className="mt-0.5 text-sm font-bold text-slate-900 md:text-base">{metric.name}</h3>
        </div>
        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${METRIC_STATUS_STYLE[metric.status]}`}>
          {METRIC_STATUS_LABEL[metric.status]}
        </span>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="text-slate-500">得分</span>
        <span className="font-semibold text-slate-900">
          {isNa ? 'N/A' : metric.score}
          <span className="text-slate-400"> / {metric.maxScore}</span>
        </span>
      </div>

      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${isNa ? 'bg-slate-200' : METRIC_STATUS_DOT[metric.status]}`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {metric.description && (
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{metric.description}</p>
      )}
      {metric.reasonCode && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
          <i className="ri-price-tag-2-line" />
          {metric.reasonCode}
        </p>
      )}
    </div>
  );
}

export default function MetricsSection({ metrics }: { metrics: MetricResult[] }) {
  const list = metrics || [];
  const merged = METRIC_DEFINITIONS.map((def) => resolveMetric(def.code, def.name, def.maxScore, list));

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 pt-12 md:px-8 md:pt-14">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-teal-50 text-teal-700">
          <i className="ri-list-check-2" />
        </span>
        <h2 className="text-lg font-bold text-slate-900 md:text-[1.5rem]">11 項健康度明細</h2>
      </div>
      <p className="mb-5 text-sm text-slate-500">
        總分 100 分。標示為「資料不足 (N/A)」的指標不列入總分計算，不會以 0 分呈現。
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 md:gap-4">
        {merged.map((metric) => (
          <MetricItem key={metric.code} metric={metric} />
        ))}
      </div>
    </section>
  );
}